import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  downloadClientInvoicePDF,
  getAllTransactionsForClient,
  selectClientTransactions,
  selectLoadingClientTransactions,
} from "./ClientTransactionsTableSlice";
import { PLClientTransactionFileModel } from "services/api/cplapi/CPLAPI";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import { faDownload, faFileInvoice, faCreditCard } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { PageDescription } from "_metronic/layout/core";
import { downloadFile } from "cpl-react-common/Helpers/FileHelpers";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";

const ClientTransactionsTable: React.FC = () => {
  const { clientID } = useParams();
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const clientTransactions = useSelector(selectClientTransactions);
  const isLoading = useSelector(selectLoadingClientTransactions);
  const globalSettings = useSelector(selectGlobalSettings);

  const columnHelper = createColumnHelper<PLClientTransactionFileModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: (info) => (
        <div className="d-flex">
          <FontAwesomeIcon
            icon={info.row.original.invoiceNumber != null ? faFileInvoice : faCreditCard}
            size="lg"
            className="cpl-brand-primary"
          />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.transactionDate, {
      id: "Transaction Date",
      cell: (info) => info.getValue()?.toCPLUserFriendlyDateString(),
      header: () => <>Transaction Date</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.invoiceNumber, {
      id: "Invoice Number",
      cell: (info) => info.getValue(),
      header: () => <>Invoice Number</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.description, {
      id: "Description",
      cell: (info) => info.getValue(),
      header: () => <>Description</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.invoiceNumber, {
      id: "Invoice Details",
      cell: (info) => (
        <>
          {info.row.original.invoiceNumber}
          <div className="text-muted">{info.row.original.description}</div>
        </>
      ),
      header: () => <>Invoice Details</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.accessor((row) => row.amount, {
      id: "Amount",
      cell: (info) => info.getValue()!.toGBPString(),
      header: () => <>Amount</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (props) =>
        props.row.original.hasChargeFiles && (
          <div
            className="cpl-btn btn-sm cpl-btn-outline"
            onClick={() => navigateToInvoice(props.row.original)}
            data-test-id={`client-transactions-table__view-charges-link-${props.row.original.transactionFileID}`}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
          >
            View Charges
          </div>
        ),
      enableHiding: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.display({
      id: "secondaryActions",
      cell: (props) => (
        <div className="d-flex justify-content-end">
          {(props.row.original.queryCaseID && (
            <div
              className="cpl-btn btn-sm cpl-btn-outline me-1"
              onClick={() => navigateToCase(props.row.original)}
              data-test-id={`client-transactions-table__view-case-link-${props.row.original.transactionFileID}`}
            >
              View Case
            </div>
          )) ||
            (props.row.original.invoiceNumber != undefined &&
              props.row.original.amount! >= 0 &&
              globalSettings.showRaiseCasesButton &&
              globalSettings.showCases && (
                <div
                  className="cpl-btn btn-sm cpl-btn-outline me-1"
                  onClick={() => navigateToQuery(props.row.original)}
                  data-test-id={`client-transactions-table__raise-case-link-${props.row.original.transactionFileID}`}
                >
                  Raise case
                </div>
              ))}
          {props.row.original.invoiceNumber != null && (
            <>
              <div>
                <a
                  className="cpl-btn btn-icon cpl-btn-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadClientInvoice(props.row.original.transactionFileID!);
                  }}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Download Invoice"
                  data-test-id={`client-transactions-table__download-invoice-button-${props.row.original.transactionFileID}`}
                  target="_blank"
                  download
                >
                  <FontAwesomeIcon icon={faDownload} />
                </a>
              </div>
            </>
          )}
        </div>
      ),
      enableHiding: false,
    }),
  ];

  useEffect(() => {
    thunkDispatch(getAllTransactionsForClient({ clientID: clientID! }));
  }, [thunkDispatch, clientID]);

  const navigateToInvoice = (transactionRow: PLClientTransactionFileModel) => {
    if (!transactionRow.hasChargeFiles) return;
    const path = `/ClientTransactions/${clientID}/${transactionRow.transactionFileID}`;
    navigate(path);
  };

  const navigateToQuery = (transactionRow: PLClientTransactionFileModel) => {
    const path = `/Case/?transaction_id=${transactionRow.transactionFileID}&invoice_number=${transactionRow.invoiceNumber}&property_id=${transactionRow.propertyID}&redirect=clienttransactions/${clientID}`;
    navigate(path);
  };

  const navigateToCase = (transactionRow: PLClientTransactionFileModel) => {
    const path = `/Case/${transactionRow.queryCaseID}`;
    navigate(path);
  };

  async function downloadClientInvoice(plClientTransactionFileID: string) {
    const file = await thunkDispatch(downloadClientInvoicePDF(plClientTransactionFileID)).unwrap();
    downloadFile(file);
  }

  return (
    <>
      <PageDescription>{`${clientTransactions.accountNumber} - ${clientTransactions.address}`}</PageDescription>
      <div className="card d-flex">
        <ActivityIndicator isBusy={isLoading}>
          <div className="card-body">
            <CPLTable
              data={clientTransactions.plClientTransactionFiles ?? []}
              columns={columnDefinition}
              rowMobileClickAction={(row) => navigateToInvoice(row)}
              sortingState={[{ id: "Transaction Date", desc: true }]}
              classNames="cpl-table-nowrap-below-desktop"
            />
          </div>
        </ActivityIndicator>
      </div>
    </>
  );
};

export default ClientTransactionsTable;
